import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import MultiSiteDiagram from '../components/MultiSiteDiagram';
import TestimonialQuote from '../components/TestimonialQuote';
import { CaseStudy, caseStudies, caseStudyPath } from '../data/CaseStudies';
import { testimonialForStudy } from '../data/Testimonials';

function CaseStudyPage({ study }: { study: CaseStudy }) {
  const others = caseStudies.filter((item) => item.id !== study.id);
  const testimonial = testimonialForStudy(study.id);
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 7 } }}
    >
      <MuiLink
        component={Link}
        to='/case-studies'
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 3, fontWeight: 700 }}
      >
        <ArrowBackIcon fontSize='small' />
        All CMS case studies
      </MuiLink>

      <Box
        component='article'
        aria-labelledby={`${study.id}-title`}
        sx={{
          border: '1px solid rgba(11, 37, 69, 0.14)',
          borderRadius: 3,
          minWidth: 0,
          p: { xs: 3, md: 5 },
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant='overline'
          sx={{ color: '#005596', fontWeight: 700, overflowWrap: 'anywhere' }}
        >
          {study.attribution}
        </Typography>
        <Typography
          component='h1'
          id={`${study.id}-title`}
          variant='h3'
          aria-label={`${study.title} — ${study.attribution}`}
          sx={{
            mt: 1,
            mb: 3,
            fontWeight: 800,
            color: '#0B2545',
            fontSize: { xs: '2rem', md: '3rem' },
            overflowWrap: 'anywhere',
          }}
        >
          {study.title}
        </Typography>
        <Box
          component='dl'
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 2, md: 4 },
          }}
        >
          {(
            [
              ['The need', study.problem],
              ['The work', study.work],
              ['What changed', study.result],
            ] as const
          ).map(([label, value]) => (
            <Box
              key={label}
              sx={
                label === 'What changed'
                  ? {
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#EFF6FC',
                      borderLeft: '4px solid #005596',
                    }
                  : undefined
              }
            >
              <Typography
                component='dt'
                variant='subtitle1'
                sx={{
                  fontWeight: 700,
                  color: label === 'What changed' ? '#0B2545' : undefined,
                }}
              >
                {label}
              </Typography>
              <Typography
                component='dd'
                variant='body1'
                sx={{ m: 0, color: label === 'What changed' ? '#0B2545' : '#475569' }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        {study.diagram && <MultiSiteDiagram />}
        {testimonial && (
          <Box sx={{ mt: 4 }}>
            <TestimonialQuote testimonial={testimonial} />
          </Box>
        )}
      </Box>

      <Box
        component='nav'
        aria-label='More case studies'
        sx={{ mt: 5 }}
      >
        <Typography
          component='h2'
          variant='h5'
          sx={{ mb: 1, fontWeight: 700, color: '#0B2545' }}
        >
          More case studies
        </Typography>
        <Box
          component='ul'
          sx={{ m: 0, pl: 3, display: 'grid', gap: 1 }}
        >
          {others.map((item) => (
            <li key={item.id}>
              <MuiLink
                component={Link}
                to={caseStudyPath(item.id)}
              >
                {item.title}
              </MuiLink>
            </li>
          ))}
        </Box>
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default CaseStudyPage;
